import analytics from './segment';
import { getCurrentCurrency } from './utils/current-currency';
import { getLocalizedPrice } from './utils/get-localized-price';
import type {
  ID,
  UserTraits,
  Options,
  Callback,
} from '@segment/analytics-next';
import ProductData from './product-data';
import images from './images.json';
import { SRP_PRODUCT_CODE } from './srp';
import { getPersistedPromoCode } from './promos';
import { PRODUCT_SKUS } from 'lib/constants/product-skus';
import {
  trackCustomAction,
  isDatadogInitialized,
  setDatadogUser,
} from './datadog-rum';
import { hashUserInfo } from './helpers/hash-user-info';

type PageViewProperties = {
  currency?: string;
  path?: string;
  url?: string;
  theme?: string;
  title?: string;
  pageCounter?: number;
  userData?: object;
};

const debugAnalytics = process.env.NEXT_PUBLIC_ANALYTICS_DEBUG;

// Shared helper functions
const logDebug = (label: string, ...args: any[]) => {
  if (debugAnalytics) console.log(`Analytics Debug: ${label}`, ...args);
};

const PRODUCT_IMAGE_MAP: Record<string, string> = {
  [PRODUCT_SKUS.DAILY_SYNBIOTIC]: images.jar,
  [PRODUCT_SKUS.DAILY_SYNBIOTIC_REFILL]: images.pouch,
  [PRODUCT_SKUS.PDS08_DAILY_KIDS_PROBIOTIC]: images.pdsJar,
  [PRODUCT_SKUS.PDS08_DAILY_KIDS_PROBIOTIC_REFILL]: images.sachet,
  [SRP_PRODUCT_CODE]: images.pouches,
};

const roundNumber = (value: number, numberOfDigits: number): number =>
  Number(value.toFixed(numberOfDigits));

const getProductImage = (sku: string): string =>
  PRODUCT_IMAGE_MAP[sku] ?? ProductData[sku]?.image ?? images.jar;

// --------------------------------------------------------------
// ALL NEW CODE SHOULD USE THESE FUNCTIONS
// (Once all call-sites are migrated, the class can be removed)
// --------------------------------------------------------------
export const identifyUser = (
  userId: ID,
  properties: UserTraits = {},
  options: Options = {},
  callback: Callback = () => null,
) => {
  userId
    ? analytics.identify(userId, properties, options, callback)
    : analytics.identify(properties, options, callback);

  (window.dataLayer || []).push(properties);

  if (userId && isDatadogInitialized()) setDatadogUser(userId);

  logDebug('Identify User', userId, properties, options, callback);
};

export const defaultUserPropertiesForUser = (user: {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}) => ({
  name: `${user.firstName} ${user.lastName}`,
  firstName: user.firstName,
  lastName: user.lastName,
  email: user.email,
  phone: user.phone,
});

// Meta CAPI user_data: hashed PII + external_id. Boosts Event Match Quality.
// trackingParameters is populated server-side (server/controllers/user.js).
// firstName/lastName/city/state/zip are hashed client-side via hashUserInfo.
// fbp / fbc are added in lib/segment.ts campaignPlugin so every track event
// gets them, including ones that don't call this helper.
export const buildMetaUserData = async (user?: {
  externalId?: string;
  firstName?: string;
  lastName?: string;
  trackingParameters?: {
    hashedEmailAddress?: string;
    hashedPhoneNumber?: string;
  };
  account?: {
    details?: {
      shipping_addresses?: Array<{
        postal_code?: string;
        city?: string;
        region?: string;
      }>;
    };
  };
}) => {
  const hashedEmail = user?.trackingParameters?.hashedEmailAddress;
  const hashedPhone = user?.trackingParameters?.hashedPhoneNumber;
  const hashed = user ? await hashUserInfo(user) : null;

  const result = {
    ...(hashedEmail ? { email: hashedEmail } : {}),
    ...(hashedPhone ? { phone: hashedPhone } : {}),
    ...(user?.externalId ? { external_id: user.externalId } : {}),
    ...(hashed?.hashedFirstName ? { firstName: hashed.hashedFirstName } : {}),
    ...(hashed?.hashedLastName ? { lastName: hashed.hashedLastName } : {}),
    ...(hashed?.hashedCity ? { city: hashed.hashedCity } : {}),
    ...(hashed?.hashedState ? { state: hashed.hashedState } : {}),
    ...(hashed?.hashedZipCode ? { zip: hashed.hashedZipCode } : {}),
  };

  return Object.keys(result).length === 0 ? undefined : result;
};

export const resetUser = () => {
  analytics.reset();
  logDebug('Reset User');
};

export const trackPageView = (
  pageName: string,
  properties: PageViewProperties = {},
) => {
  const localizedProperties = {
    currency: getCurrentCurrency(),
    reduced_motion: window.matchMedia('(prefers-reduced-motion: reduce)')
      .matches,
    ...properties,
  };
  analytics.page(pageName, localizedProperties);
  logDebug('Page View', pageName, localizedProperties);
};

export const trackEvent = (
  eventName: string,
  properties: { [key: string]: any } = {},
  category: string = 'Default',
  options: { [key: string]: any } = {},
) => {
  analytics.track(eventName, properties, options);

  if (isDatadogInitialized()) {
    // Keep PII out of RUM.
    const {
      email,
      phone,
      userData,
      memberFirstName,
      memberLastName,
      address,
      currentSelectedAddress,
      ...rumProperties
    } = properties;
    trackCustomAction(eventName, {
      ...rumProperties,
      category,
      source: 'analytics_manager',
    });
  }

  logDebug('Event', eventName, properties, category);
};

export const trackButtonClicked = (label: string, sku: string) =>
  trackEvent('Button Clicked', {
    buttonName: label,
    location: window.location.pathname,
    url: window.location.href,
    path: window.location.pathname,
    product_sku: sku,
  });

export const trackButtonEvent = (
  buttonName: string,
  location: string,
  properties: { [key: string]: any } = {},
) =>
  trackEvent('Button Clicked', {
    buttonName,
    location,
    url: window.location.href,
    path: window.location.pathname,
    ...properties,
  });

export const trackTextLinkClicked = (
  textLink: string,
  location: string,
  properties: { [key: string]: any } = {},
) => trackEvent('Button Clicked', { textLink, location, ...properties });

export const trackLinkClicked = (
  buttonName: string,
  buttonURL: string,
  location: string,
  path: string,
  url: string,
  properties?: { [key: string]: any },
) =>
  trackEvent('Link Clicked', {
    buttonName,
    buttonURL,
    location,
    path,
    url,
    textLink: null,
    ...properties,
  });

export const identifyAndTrackEvent = (
  userId: ID,
  userTraits: UserTraits = {},
  eventName: string,
  eventProperties: Record<string, any> = {},
  eventCategory: string = 'Default',
) => {
  logDebug(
    'Identify and Track',
    userId,
    userTraits,
    eventName,
    eventProperties,
    eventCategory,
  );

  identifyUser(userId, userTraits, {}, () => {
    trackEvent(eventName, eventProperties, eventCategory);
    return null;
  });
};

export const propertiesForProduct = (
  product: any,
  quantity = 1,
  planId: string | null = null,
) => {
  // Currency and amount must move together: `currency` below is the live
  // market currency, so the amount must be the localized price.
  const price = Number(getLocalizedPrice(product));
  const sku = product?.productSku || '';
  const productId = `${product?.id || ''}`;
  // content_ids / content_type / contents feed Meta CAPI ViewContent + AddToCart
  // via Segment's Meta destination. Without them EMQ stays low.
  const contentId = sku || productId;
  const itemPrice = Number.isFinite(price) ? price : 0;
  return {
    product_id: productId,
    plan_id: planId || '',
    sku,
    category: 'Probiotic',
    name: product?.name || '',
    brand: 'Seed',
    price,
    value: price,
    quantity,
    coupon: getPersistedPromoCode() || '',
    url: product
      ? `https://seed.com/${ProductData[product.productSku]?.cmsSlug}`
      : '',
    image_url: getProductImage(product?.productSku),
    subtotal: roundNumber(price * quantity, 2),
    currency: getCurrentCurrency(),
    content_ids: contentId ? [contentId] : [],
    content_type: 'product',
    contents: contentId
      ? [{ id: contentId, quantity, item_price: itemPrice }]
      : [],
    num_items: quantity,
  };
};

// Analytics Event Constants
// Centralized location for all analytics tracking event names
export const CHECKOUT_EVENTS = {
  CHECKOUT_STARTED: 'Checkout Started',
  CHECKOUT_STEP_VIEWED: 'Checkout Step Viewed',
  CHECKOUT_STEP_COMPLETED: 'Checkout Step Completed',
  CHECKOUT_STEP_FAILED: 'Checkout Step Failed',
  CHECKOUT_ERRORS: 'Checkout Errors',
  CHECKOUT_BUTTON_CLICKED: 'Checkout button clicked',
} as const;

export const CART_EVENTS = {
  CART_VIEWED: 'Cart Viewed',
  PRODUCT_ADDED: 'Product Added',
  PRODUCT_REMOVED: 'Product Removed',
  ITEM_REMOVED_FROM_CART: 'Item removed from cart',
} as const;

export const PAYMENT_EVENTS = {
  PAYMENT_METHOD_CLICKED: 'Payment Method Clicked',
  PAYMENT_METHOD_SELECTED: 'Payment Method Selected',
  PAYMENT_METHOD_SUBMITTED: 'Payment Method Submitted',
  PAYMENT_METHOD_ERROR: 'Payment Method Error',
  PAYMENT_INFO_ENTERED: 'Payment Info Entered',
} as const;

export const EXPRESS_PAY_EVENTS = {
  EXPRESS_PAY_STARTED: 'Express Pay Started',
  EXPRESS_PAY_COMPLETED: 'Express Pay Completed',
  EXPRESS_PAY_FAILED: 'Express Pay Failed',
  EXPRESS_PAY_SHIPPING_CONTACT_SELECTED:
    'Express Pay Shipping Contact Selected',
  EXPRESS_PAY_PAYMENT_METHOD_SELECTED: 'Express Pay Payment Method Selected',
  EXPRESS_PAY_INTERNATIONAL_SHIPPING_ERROR:
    'Express Pay International Shipping Error',
  EXPRESS_PAY_PURCHASE_PREVIEW_ERROR: 'Express Pay Purchase Preview Error',
} as const;

export const COUPON_EVENTS = {
  COUPON_APPLIED: 'Coupon Applied',
  COUPON_DENIED: 'Coupon Denied',
  COUPON_FRAUD: 'Coupon Fraud',
  COUPON_REMOVED: 'Coupon Removed',
  PROMO_FIELD_OPENED: 'Promo Field Opened',
} as const;

export const USER_EVENTS = {
  USER_REGISTERED: 'user_registered',
  LOGIN: 'Login',
  EMAIL_OPT_IN: 'Email Opt In',
  SMS_OPT_IN: 'SMS Opt In',
} as const;

export const UI_EVENTS = {
  BUTTON_CLICKED: 'Button Clicked',
  LINK_CLICKED: 'Link Clicked',
  OPENS_CART_ORDER_SUMMARY: 'Opens Cart Order Summary',
  CLOSES_CART_ORDER_SUMMARY: 'Closes Cart Order Summary',
  OPENS_BILLING_ADDRESS: 'Opens Billing Address',
  SHIPPING_METHOD_SELECTED: 'Shipping Method Selected',
} as const;

export const ERROR_EVENTS = {
  TURNSTILE_ERROR: 'Turnstile Error',
  AMAZON_PAY_CANCELLED: 'Amazon Pay Cancelled',
  PAYPAL_CANCELLED: 'PayPal Cancelled',
  CHECKOUT_FIRST_OR_LAST_EMPTY: 'Checkout First or Last Empty',
} as const;

export const MODAL_EVENTS = {
  PRICE_TEST_MODAL_VIEWED: 'Price Test Modal Viewed',
} as const;

// Combined events object
export const ANALYTICS_EVENTS = {
  ...CHECKOUT_EVENTS,
  ...CART_EVENTS,
  ...PAYMENT_EVENTS,
  ...EXPRESS_PAY_EVENTS,
  ...COUPON_EVENTS,
  ...USER_EVENTS,
  ...UI_EVENTS,
  ...ERROR_EVENTS,
  ...MODAL_EVENTS,
} as const;

// Type definitions
export type CheckoutEventName =
  (typeof CHECKOUT_EVENTS)[keyof typeof CHECKOUT_EVENTS];
export type CartEventName = (typeof CART_EVENTS)[keyof typeof CART_EVENTS];
export type PaymentEventName =
  (typeof PAYMENT_EVENTS)[keyof typeof PAYMENT_EVENTS];
export type AnalyticsEventName =
  (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];
